const deployments = [
  {
    id: "private",
    name: "Private",
    tag: "your VPC",
    badge: "Recommended for regulated",
    desc: "The full stack — Go API, Postgres, the COMPL-AI eval worker, and the artifact store — runs inside your own infrastructure.",
    pros: [
      "Single-host, Swarm, or fully air-gapped",
      "Model keys and outputs never leave your perimeter",
      "No internet egress required",
    ],
    cons: ["You operate it", "Higher initial setup"],
    specs: [
      { label: "Time to first report", value: "1–2 weeks" },
      { label: "Best for", value: "Regulated teams, EU sovereignty" },
    ],
  },
  {
    id: "hosted",
    name: "Hosted",
    tag: "managed",
    badge: "Fastest to start",
    desc: "We run the evaluation on our infrastructure. Multi-tenant key isolation keeps every customer&apos;s secrets namespaced.",
    pros: [
      "Zero deployment — up and running in minutes",
      "Ideal for OpenAI, Azure, Anthropic endpoints",
      "DPA-governed",
    ],
    cons: ["Prompts traverse our infra", "No self-hosted models"],
    specs: [
      { label: "Time to first report", value: "< 1 day" },
      { label: "Best for", value: "Cloud-native teams, quick start" },
    ],
  },
  {
    id: "hybrid",
    name: "Hybrid",
    tag: "Best of both",
    badge: "Best of both",
    desc: "The control plane is hosted; the eval worker runs inside your network. Models stay put, the UI stays accessible.",
    pros: [
      "UI / dashboards accessible from anywhere",
      "Eval worker runs against your models in-VPC",
      "DPA + your keys in your perimeter",
    ],
    cons: ["Two deployments to coordinate"],
    specs: [
      { label: "Time to first report", value: "1 week" },
      { label: "Best for", value: "Mixed portfolios, multi-team access" },
    ],
  },
];

export default function Deploy() {
  return (
    <section id="deploy" className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="section-head">
          <p className="eyebrow">
            <i className="ri-server-line" />
            Deployment
          </p>
          <h2 className="section-title">
            Pick the shape that fits your stack
          </h2>
          <p className="section-lede">
            Same engine, same evidence format. Choose how it lives next to your model.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
          {deployments.map((dep) => (
            <article
              key={dep.id}
              className={`card ${dep.id === "private" ? "border-primary-300/60 ring-1 ring-primary-300/30" : ""} flex flex-col`}
            >
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg md:text-xl font-semibold text-foreground-900">
                    {dep.name}
                  </h3>
                  <span className="text-xs text-foreground-500">· {dep.tag}</span>
                </div>
                <span className={`inline-block text-[10px] md:text-xs font-medium px-2 py-0.5 rounded-full ${
                  dep.id === "private"
                    ? "bg-primary-100 text-primary-700"
                    : dep.id === "hosted"
                    ? "bg-accent-100 text-accent-700"
                    : "bg-secondary-100 text-secondary-700"
                }`}>
                  {dep.badge}
                </span>
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                {dep.desc}
              </p>

              <ul className="space-y-1.5 mb-3">
                {dep.pros.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-check-line text-primary-500 shrink-0 mt-0.5" />
                    {p}
                  </li>
                ))}
              </ul>

              <ul className="space-y-1.5 mb-5">
                {dep.cons.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-sm text-foreground-500">
                    <i className="ri-subtract-line text-foreground-400 shrink-0 mt-0.5" />
                    {c}
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-4 border-t border-background-200/70 space-y-2">
                {dep.specs.map((spec) => (
                  <div key={spec.label} className="flex items-baseline justify-between">
                    <dt className="text-xs text-foreground-500">{spec.label}</dt>
                    <dd className="text-sm font-medium text-foreground-800">{spec.value}</dd>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}